/**
 * Centrale bron van waarheid voor USP's en beschikbare betaalmethoden.
 *
 * Alle publieke pagina's (header, productpagina's, /betaalmethoden,
 * klantenservice, voorwaarden) moeten deze constanten gebruiken zodat er
 * nooit meer betaalmethoden of retourvoorwaarden worden getoond die niet
 * daadwerkelijk in de checkout beschikbaar zijn.
 *
 * Niet beschikbaar (dus NOOIT tonen): Klarna, Riverty, AfterPay, PayPal of
 * andere achteraf-betaalmethoden voor consumenten.
 */

/** USP-balk, identiek op elke pagina. */
export const USPS = [
  "Gratis verzending in NL vanaf €150",
  "Bezorging 1 tot 2 werkdagen",
  "14 dagen bedenktijd",
  "Afhalen in Wageningen",
] as const;

/**
 * Standaard verzendzin. Gebruik deze overal (productpagina, checkout,
 * voorwaarden, verzendbeleid, klantenservice) zodat de communicatie identiek is.
 */
export const SHIPPING_POLICY_TEXT =
  "Levering binnen Nederland en België. Verzendkosten worden vóór betaling getoond; alleen binnen Nederland gratis verzending vanaf €150.";

/** Standaardzin waar een algemene omschrijving van betaling nodig is. */
export const PAYMENT_GENERIC_TEXT =
  "De beschikbare betaalmethoden worden tijdens het bestelproces weergegeven. De klant kan uitsluitend gebruikmaken van de betaalmethoden die op dat moment in de checkout beschikbaar zijn.";

export type PaymentMethodId =
  | "wero"
  | "visa"
  | "mastercard"
  | "apple-pay"
  | "google-pay";

/** Betaalmethode-badges die op productpagina's getoond worden. */
export const PAYMENT_BADGES: PaymentMethodId[] = [
  "wero",
  "visa",
  "mastercard",
  "apple-pay",
  "google-pay",
];

export type PaymentMethod = {
  name: string;
  desc: string;
  fee: string;
  time: string;
};

/** Betaalmethoden voor particulieren — gelijk aan de live checkout. */
export const CONSUMER_PAYMENT_METHODS: PaymentMethod[] = [
  { name: "iDEAL", desc: "Direct betalen via uw eigen bank (Nederland).", fee: "Geen", time: "Direct" },
  { name: "Bancontact", desc: "Direct betalen via uw Belgische bank.", fee: "Geen", time: "Direct" },
  { name: "Creditcard", desc: "Visa, Mastercard en American Express met 3D Secure.", fee: "Geen", time: "Direct" },
  { name: "Apple Pay", desc: "Betalen met Touch ID of Face ID op Apple-apparaten.", fee: "Geen", time: "Direct" },
  { name: "Google Pay", desc: "Betalen met uw opgeslagen kaart in Google Wallet.", fee: "Geen", time: "Direct" },
  { name: "SEPA-overschrijving", desc: "Handmatige bankoverschrijving met betaalkenmerk.", fee: "Geen", time: "1–2 werkdagen" },
];

/** Betaalmethoden voor zakelijke klanten na verificatie. */
export const BUSINESS_PAYMENT_METHODS: PaymentMethod[] = [
  {
    name: "Op factuur (zakelijk)",
    desc: "Betaling binnen 14 dagen na factuurdatum. Uitsluitend voor geverifieerde zakelijke klanten na goedkeuring.",
    fee: "Geen",
    time: "14 dagen",
  },
  {
    name: "Aanbetaling + slotbetaling",
    desc: "Bij grotere installatieopdrachten: aanbetaling vooraf, restant na oplevering.",
    fee: "Geen",
    time: "Volgens offerte",
  },
];

/**
 * Tijdelijke bestelstop. Zet op `true` om bestellen weer mogelijk te maken.
 * Wanneer uit: geen winkelwagen-/afrekenknoppen, maar een melding met
 * doorverwijzing naar offerte/contact.
 */
export const ORDERING_ENABLED = false;

/** Melding die getoond wordt zolang bestellen uitstaat. */
export const ORDERING_DISABLED_TEXT =
  "Online bestellen is tijdelijk niet mogelijk. Vraag een offerte aan of neem contact met ons op — we helpen u direct verder.";

/**
 * Tijdelijk: vervang alle prijzen door "Prijs op aanvraag".
 * Zet op `false` om normale prijzen weer te tonen.
 */
export const PRICE_ON_REQUEST = true;

/** Label dat getoond wordt wanneer PRICE_ON_REQUEST aan staat. */
export const PRICE_ON_REQUEST_LABEL = "Prijs op aanvraag";
